import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowDown, ArrowUp, ChevronDown, ChevronLeft, ChevronRight, ChevronRight as Caret, Columns3,
  Download, Rows3, Search, X,
} from 'lucide-react';
import Spinner from './Spinner';
import EmptyState from './EmptyState';

/**
 * Data-dense table for CRM-style listings.
 *
 * Columns are declared as:
 *   { key, header, render?, sortValue?, className?, align?, width?,
 *     sortable?, exportValue?, hideable?, defaultHidden? }
 *
 * Sorting/filtering/pagination run client-side over `rows`. For server-side
 * paging, pass `serverMode` and drive `rows` yourself.
 */
export default function DataTable({
  columns,
  rows,
  rowKey = (r) => r.id,
  loading = false,
  // search
  searchable = true,
  searchPlaceholder = 'Search…',
  searchKeys,
  // selection
  selectable = false,
  bulkActions,            // (selectedRows, clear) => ReactNode
  // export
  exportable = false,
  exportFilename = 'export',
  // paging
  pageSizes = [25, 50, 100],
  initialPageSize = 25,
  // misc
  storageKey,             // persists column visibility + density
  emptyTitle = 'Nothing here yet',
  emptyHint,
  toolbarExtra,
  onRowClick,
  // (row) => ReactNode — when given, each row gets a caret that opens a panel
  // underneath it. Returning null leaves that row with no caret.
  renderExpanded,
  onSearchEnter,          // (query, matchCount) => void — Enter in the search box
  searchAddLabel,         // string, or (query) => string|null to decide per query — shows "+ Add “query” — new, press Enter" under the toolbar
}) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [selected, setSelected] = useState(() => new Set());
  // Which row has its detail panel open — one at a time, so the row being
  // read is never in doubt and the table does not grow a panel per row.
  const [openRow, setOpenRow] = useState(null);

  const prefs = usePrefs(storageKey, columns);
  const { hidden, toggleColumn, density, setDensity } = prefs;

  const visibleColumns = useMemo(
    () => columns.filter((c) => !hidden.has(c.key)),
    [columns, hidden],
  );

  // ---- filter ----------------------------------------------------------
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    const keys = searchKeys || columns.map((c) => c.key);
    return rows.filter((row) =>
      keys.some((k) => {
        const col = columns.find((c) => c.key === k);
        const v = col?.exportValue ? col.exportValue(row) : row[k];
        return v != null && String(v).toLowerCase().includes(q);
      }),
    );
  }, [rows, query, searchKeys, columns]);

  // ---- sort ------------------------------------------------------------
  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const val = (r) => (col.sortValue ? col.sortValue(r) : r[col.key]);
    return [...filtered].sort((a, b) => {
      const av = val(a); const bv = val(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const n = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return sort.dir === 'asc' ? n : -n;
    });
  }, [filtered, sort, columns]);

  // ---- page ------------------------------------------------------------
  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageRows = useMemo(
    () => sorted.slice((safePage - 1) * pageSize, safePage * pageSize),
    [sorted, safePage, pageSize],
  );

  useEffect(() => { setPage(1); }, [query, pageSize]);

  // ---- selection -------------------------------------------------------
  const pageKeys = pageRows.map(rowKey);
  const allOnPageSelected = pageKeys.length > 0 && pageKeys.every((k) => selected.has(k));
  const someOnPageSelected = pageKeys.some((k) => selected.has(k));

  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageKeys.forEach((k) => next.delete(k));
      else pageKeys.forEach((k) => next.add(k));
      return next;
    });
  };
  const toggleRow = (k) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const selectedRows = rows.filter((r) => selected.has(rowKey(r)));

  const addLabel = onSearchEnter && query.trim()
    ? (typeof searchAddLabel === 'function' ? searchAddLabel(query.trim()) : searchAddLabel)
    : null;
  const cellPad = density === 'compact' ? 'px-3 py-1.5' : 'px-4 py-2.5';
  const expandable = typeof renderExpanded === 'function';
  const toggleExpanded = (k) => setOpenRow((prev) => (prev === k ? null : k));
  // The detail panel spans the whole table, so it needs the live column count.
  const bodyCols = visibleColumns.length + (selectable ? 1 : 0) + (expandable ? 1 : 0);

  return (
    <div className="card mod-card overflow-hidden">
      {/* ---- Toolbar ---- */}
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2.5">
        {searchable && (
          <div className="relative min-w-[12rem] flex-1 sm:max-w-xs">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle" />
            <input
              className="input input-sm pl-8 pr-7"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchEnter ? (e) => { if (e.key === 'Enter') onSearchEnter(query.trim(), total, !!addLabel); } : undefined}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content"
                aria-label="Clear search"
              >
                <X size={13} />
              </button>
            )}
          </div>
        )}

        <span className="text-2xs text-content-subtle">
          {total.toLocaleString()} {total === 1 ? 'record' : 'records'}
          {query && rows.length !== total && ` of ${rows.length.toLocaleString()}`}
        </span>

        {/* This group does not wrap on its own, so on a phone the date
            filter inside it pushed Columns and Export clean off the screen.
            Full width and wrapping below `sm`; unchanged on a desk machine. */}
        <div className="flex w-full flex-wrap items-center gap-1.5 sm:ml-auto sm:w-auto sm:flex-nowrap">
          {toolbarExtra}

          <Menu
            icon={Rows3}
            label="Density"
            items={[
              { label: 'Comfortable', active: density === 'comfortable', onClick: () => setDensity('comfortable') },
              { label: 'Compact', active: density === 'compact', onClick: () => setDensity('compact') },
            ]}
          />

          <Menu
            icon={Columns3}
            label="Columns"
            items={columns.filter((c) => c.hideable !== false).map((c) => ({
              label: c.header,
              active: !hidden.has(c.key),
              checkbox: true,
              onClick: () => toggleColumn(c.key),
            }))}
          />

          {exportable && (
            <Menu
              icon={Download}
              label="Export"
              items={[
                { label: 'CSV', onClick: () => exportCsv(visibleColumns, sorted, exportFilename) },
                { label: 'Excel (.xls)', onClick: () => exportExcel(visibleColumns, sorted, exportFilename) },
              ]}
            />
          )}
        </div>
      </div>

      {/* ---- Add-new strip: the typed search becomes a new record on Enter ---- */}
      {addLabel && (
        <button
          type="button"
          onClick={() => onSearchEnter(query.trim(), total)}
          className="flex w-full items-center gap-1.5 border-b border-line bg-brand-100 px-4 py-2.5 text-left text-sm font-bold text-brand-800 hover:bg-brand-200"
        >
          + {addLabel} “{query.trim()}” <span className="font-normal text-content-subtle">— new, press Enter</span>
        </button>
      )}
      {/* ---- Bulk action bar ---- */}
      {selectable && selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-line bg-brand-50 px-3 py-2">
          <span className="text-xs font-semibold text-brand-700">
            {selected.size} selected
          </span>
          <button onClick={clearSelection} className="text-xs text-brand-600 underline-offset-2 hover:underline">
            Clear
          </button>
          <div className="ml-auto flex items-center gap-1.5">
            {bulkActions?.(selectedRows, clearSelection)}
          </div>
        </div>
      )}

      {/* ---- Table ---- */}
      <div className="table-scroll overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              {expandable && <th className="th w-9" aria-label="Expand" />}
              {selectable && (
                <th className="th w-10">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 cursor-pointer accent-brand-600"
                    checked={allOnPageSelected}
                    ref={(el) => { if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected; }}
                    onChange={toggleAllOnPage}
                    aria-label="Select all on this page"
                  />
                </th>
              )}
              {visibleColumns.map((c) => {
                const isSorted = sort.key === c.key;
                const sortable = c.sortable !== false;
                return (
                  <th
                    key={c.key}
                    className={`th ${c.align === 'right' ? 'text-right' : ''} ${c.width || ''} ${sortable ? 'cursor-pointer select-none hover:text-content' : ''}`}
                    onClick={sortable ? () => setSort((s) => ({
                      key: c.key,
                      dir: s.key === c.key && s.dir === 'asc' ? 'desc' : 'asc',
                    })) : undefined}
                  >
                    <span className={`inline-flex items-center gap-1 ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                      {c.header}
                      {sortable && (
                        isSorted
                          ? (sort.dir === 'asc' ? <ArrowUp size={11} className="text-brand-600" /> : <ArrowDown size={11} className="text-brand-600" />)
                          : <ArrowUp size={11} className="opacity-0 transition-opacity group-hover:opacity-40" />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              const k = rowKey(row);
              const isSel = selected.has(k);
              const panel = expandable ? renderExpanded(row) : null;
              const isOpen = openRow === k;
              return (
                <Fragment key={k}>
                <tr
                  className={`row ${isSel ? 'bg-brand-50/60' : ''} ${isOpen ? 'row-open' : ''} ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={onRowClick ? (e) => {
                    if (e.target.closest('button, a, input, select')) return;
                    onRowClick(row);
                  } : undefined}
                >
                  {expandable && (
                    <td className={`${cellPad} !px-1`}>
                      {panel && (
                        <button
                          type="button"
                          className="btn-icon h-7 w-7"
                          aria-expanded={isOpen}
                          aria-label={isOpen ? 'Hide details' : 'Show details'}
                          title={isOpen ? 'Hide details' : 'Show details'}
                          onClick={(e) => { e.stopPropagation(); toggleExpanded(k); }}
                        >
                          <Caret size={15} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                        </button>
                      )}
                    </td>
                  )}
                  {selectable && (
                    <td className={cellPad}>
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 cursor-pointer accent-brand-600"
                        checked={isSel}
                        onChange={() => toggleRow(k)}
                        aria-label="Select row"
                      />
                    </td>
                  )}
                  {visibleColumns.map((c) => (
                    <td
                      key={c.key}
                      className={`${cellPad} text-sm text-content ${c.align === 'right' ? 'text-right' : ''} ${c.className || ''}`}
                    >
                      {c.render ? c.render(row) : row[c.key] ?? <span className="text-content-subtle">—</span>}
                    </td>
                  ))}
                </tr>
                {isOpen && panel && (
                  <tr className="row-detail">
                    <td colSpan={bodyCols} className="p-0">
                      {panel}
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        {loading && <Spinner />}
        {!loading && total === 0 && <EmptyState title={query ? 'No matches' : emptyTitle} hint={query ? `Nothing matches “${query}”.` : emptyHint} />}
      </div>

      {/* ---- Footer / pagination ---- */}
      {total > 0 && (
        <div className="flex flex-wrap items-center gap-3 border-t border-line px-3 py-2">
          <label className="flex items-center gap-1.5 text-2xs text-content-muted">
            Rows
            <select
              className="input input-sm w-auto py-1"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {pageSizes.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>

          <span className="text-2xs text-content-subtle">
            {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, total)} of {total.toLocaleString()}
          </span>

          <div className="ml-auto flex items-center gap-1">
            <button
              className="btn-icon h-7 w-7"
              disabled={safePage <= 1}
              onClick={() => setPage(safePage - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft size={15} />
            </button>
            <span className="px-1 text-2xs tabular-nums text-content-muted">
              {safePage} / {pageCount}
            </span>
            <button
              className="btn-icon h-7 w-7"
              disabled={safePage >= pageCount}
              onClick={() => setPage(safePage + 1)}
              aria-label="Next page"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */

function usePrefs(storageKey, columns) {
  const key = storageKey ? `erp_table_${storageKey}` : null;

  const [state, setState] = useState(() => {
    const fallback = {
      hidden: columns.filter((c) => c.defaultHidden).map((c) => c.key),
      density: 'comfortable',
    };
    if (!key) return fallback;
    try {
      const saved = JSON.parse(localStorage.getItem(key) || 'null');
      return saved ? { ...fallback, ...saved } : fallback;
    } catch {
      return fallback;
    }
  });

  useEffect(() => {
    if (!key) return;
    try { localStorage.setItem(key, JSON.stringify(state)); } catch { /* ignore */ }
  }, [key, state]);

  return {
    hidden: new Set(state.hidden),
    density: state.density,
    setDensity: (density) => setState((s) => ({ ...s, density })),
    toggleColumn: (colKey) => setState((s) => ({
      ...s,
      hidden: s.hidden.includes(colKey)
        ? s.hidden.filter((k) => k !== colKey)
        : [...s.hidden, colKey],
    })),
  };
}

/**
 * Toolbar dropdown. The panel is rendered in a portal because the table sits
 * inside `.card { overflow: hidden }` — an in-flow absolute panel gets clipped
 * once it is taller than the toolbar (e.g. the Columns list on wide tables).
 */
function Menu({ icon: Icon, label, items }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  // Anchor the panel under the button, flipping upward when it would run off
  // the bottom of the viewport.
  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const MENU_MAX = 320;
    const below = window.innerHeight - r.bottom - 12;
    const flip = below < Math.min(MENU_MAX, items.length * 30 + 16) && r.top > below;
    // The panel used to be pinned to the button's right edge, which on a
    // phone threw it off the left of the screen — a 192px list hanging off a
    // button 84px in has nowhere to go. It is now measured and clamped to the
    // viewport, so it stays on screen wherever the button happens to sit.
    const width = Math.min(192, window.innerWidth - 16);
    const left = Math.min(Math.max(8, r.right - width), window.innerWidth - width - 8);
    setPos({
      top: flip ? undefined : r.bottom + 4,
      bottom: flip ? window.innerHeight - r.top + 4 : undefined,
      left,
      width,
      maxHeight: Math.max(140, (flip ? r.top : below) - 8),
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onClick = (e) => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    // Reposition rather than drift if the page moves under the menu.
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        className="btn-outline btn-sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Icon size={13} />
        <span className="hidden sm:inline">{label}</span>
        <ChevronDown size={12} className="text-content-subtle" />
      </button>
      {open && pos && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: pos.top,
            bottom: pos.bottom,
            left: pos.left,
            width: pos.width,
            maxHeight: pos.maxHeight,
          }}
          className="z-[60] animate-slide-up overflow-y-auto rounded-lg border border-line bg-surface-raised py-1 shadow-pop"
        >
          {items.map((it) => (
            <button
              key={it.label}
              onClick={() => { it.onClick(); if (!it.checkbox) setOpen(false); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-content transition-colors hover:bg-surface-hover"
            >
              {it.checkbox ? (
                <input type="checkbox" readOnly checked={it.active} className="h-3.5 w-3.5 shrink-0 accent-brand-600" />
              ) : (
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${it.active ? 'bg-brand-600' : 'bg-transparent'}`} />
              )}
              <span className="min-w-0 flex-1 truncate">{it.label}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}

/* ---- export helpers --------------------------------------------------- */

function cellText(col, row) {
  if (col.exportValue) return col.exportValue(row);
  const v = row[col.key];
  return v == null ? '' : String(v);
}

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function exportCsv(columns, rows, filename) {
  const esc = (s) => `"${String(s).replace(/"/g, '""')}"`;
  const lines = [
    columns.map((c) => esc(c.header)).join(','),
    ...rows.map((r) => columns.map((c) => esc(cellText(c, r))).join(',')),
  ];
  // BOM keeps Excel happy with UTF-8 (₹, accented names).
  download(new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' }), `${filename}.csv`);
}

// dd-mm-yyyy, which Excel would otherwise read as a date, reformat, and show
// as ##### in a narrow column.
const DMY = /^\d{2}-\d{2}-\d{4}$/;

function exportExcel(columns, rows, filename) {
  const esc = (s) => String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // \@ is Excel's "this is text" number format.
  const cell = (c, r) => {
    const v = cellText(c, r);
    return DMY.test(String(v ?? ''))
      ? `<td style="mso-number-format:'\\@'">${esc(v)}</td>`
      : `<td>${esc(v)}</td>`;
  };
  const html = `<table border="1"><thead><tr>${
    columns.map((c) => `<th>${esc(c.header)}</th>`).join('')
  }</tr></thead><tbody>${
    rows.map((r) => `<tr>${columns.map((c) => cell(c, r)).join('')}</tr>`).join('')
  }</tbody></table>`;
  download(
    new Blob([`﻿<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body>${html}</body></html>`],
      { type: 'application/vnd.ms-excel;charset=utf-8;' }),
    `${filename}.xls`,
  );
}
